import {
  contextHash,
  DEFAULT_MEAL_SLOTS,
  profileSchema,
  userPreferencesSchema,
  type Availability,
  type Limitation,
  type Profile,
  type UserPreferences,
} from '@cadencia/contracts';
import { db } from '../db.js';
import { newSyncColumns, putRow, type WriteContext } from '../writes.js';
import { parchear } from '../parchear.js';

/**
 * `profiles` y `user_preferences` son SINGLETONES por usuario: la base tiene
 * `profiles_one_per_user` y `user_preferences_one_per_user`, un indice unico
 * sobre `user_id`.
 *
 * Por eso su `id` se deriva del usuario en vez de sortearse. Con un UUID nuevo
 * en cada dispositivo, el segundo en escribir manda un INSERT con otro `id`
 * sobre un `user_id` que ya tiene fila: `sync_push` resuelve por
 * `on conflict (id)`, que no encaja con ninguna, y el servidor devuelve
 * `23505 duplicate key value violates unique constraint`. La operacion no se
 * reintenta con exito **nunca**: queda rechazada en el outbox y ese
 * dispositivo pierde en silencio todo lo que escriba en esa tabla.
 *
 * Con `id = userId` los dos dispositivos escriben el mismo `id`, el
 * `on conflict (id) do update` convierte el segundo INSERT en una
 * actualizacion, y la politica de conflicto por entidad decide quien gana,
 * que es lo que corresponde.
 *
 * Lo encontro E2E-7 el 31/08/2026, en cuanto la pantalla de «IA y coste» se
 * convirtio en el primer sitio de la aplicacion que llamaba a
 * `savePreferences`. Estaba latente desde la Fase 1.
 *
 * Solo afecta a la fila que se CREA sin tener copia local: si ya hay una, se
 * conserva su `id`, incluido el aleatorio de antes de este cambio.
 */
function idDeSingleton(ctx: WriteContext): string {
  return ctx.userId;
}

/**
 * El id de un perfil NUEVO, sabiendo que puede haber lápidas de perfiles
 * anteriores (docs/TEST_INICIAL.md §2.6).
 *
 * Al borrar el historial, el perfil se borra con una lápida, y una lápida gana
 * siempre (`resolveDocumentConflict`, `tombstone_wins`; CLAUDE.md, «Resucitar
 * una fila borrada no llega a los dispositivos»). Si el test inicial creara el
 * perfil nuevo con el id de siempre —el del usuario—, el servidor lo
 * descartaría como `superseded` y la bajada lo volvería a borrar en el propio
 * dispositivo: el atleta contestaría el test y perdería el perfil en silencio.
 *
 * Así que, mientras el id candidato tenga lápida, se pasa a su SUCESOR, que se
 * deriva del anterior con SHA-256. Derivado y no aleatorio, por lo mismo que
 * `idDeSingleton`: dos dispositivos que vean la misma lápida llegan al mismo id
 * y convergen, en vez de chocar en `profiles_one_per_user` —que solo cuenta
 * perfiles vivos, así que las lápidas no estorban—.
 */
async function idDePerfilNuevo(ctx: WriteContext): Promise<string> {
  const lapidas = new Set(
    (await ctx.db.profiles.where('userId').equals(ctx.userId).toArray())
      .filter((fila) => fila.deletedAt !== null)
      .map((fila) => fila.id),
  );
  let id = idDeSingleton(ctx);
  while (lapidas.has(id)) id = await sucesorDeId(id);
  return id;
}

/** Un UUID v8 determinista a partir de otro. Exportado para sus pruebas. */
export async function sucesorDeId(id: string): Promise<string> {
  const h = await contextHash({ perfilSucesorDe: id });
  const variante = ((Number.parseInt(h[16] ?? '0', 16) & 0x3) | 0x8).toString(16);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-8${h.slice(13, 16)}-${variante}${h.slice(17, 20)}-${h.slice(20, 32)}`;
}

export async function getProfile(userId: string): Promise<Profile | undefined> {
  const rows = await db.profiles.where('userId').equals(userId).toArray();
  return rows.find((row) => row.deletedAt === null);
}

export interface ProfileInput {
  displayName: string;
  sex: Profile['sex'];
  birthDate: string | null;
  heightCm: number;
  experienceYears: number;
  trainingLevel: Profile['trainingLevel'];
  detrainedSince: string | null;
  timezone: string;
  /**
   * Lo que pregunta el test inicial (docs/TEST_INICIAL.md). Opcionales porque
   * `Más → Perfil` no los edita: ausentes, un perfil que ya existe conserva
   * los suyos.
   */
  availability?: Availability | null | undefined;
  limitations?: Limitation[] | undefined;
}

export async function saveProfile(ctx: WriteContext, entrada: ProfileInput): Promise<Profile> {
  const existing = await getProfile(ctx.userId);
  // Sin las claves que vienen a `undefined`: `parchear` mezcla con un spread, y
  // un `availability: undefined` explícito borraría la disponibilidad guardada
  // —el contrato la convierte en `null`— al editar el perfil desde otra
  // pantalla que no la conoce.
  // El tipo que resulta es ese: ninguna clave presente vale `undefined`.
  const input = Object.fromEntries(
    Object.entries(entrada).filter(([, valor]) => valor !== undefined),
  ) as Partial<Profile>;

  // Validacion en el limite del sistema, antes de tocar la base local. Sobre
  // una fila que YA existe se parchea: si la guardada no encajaba, negarse a
  // editar el perfil no la arregla y deja al usuario sin poder tocar sus
  // datos (R-19).
  const parsed = existing
    ? parchear(profileSchema, existing, input, { tabla: 'profiles', id: existing.id })
    : profileSchema.parse({ ...newSyncColumns(ctx, await idDePerfilNuevo(ctx)), ...input });
  return putRow(ctx, 'profiles', parsed);
}

export async function getPreferences(userId: string): Promise<UserPreferences | undefined> {
  const rows = await db.userPreferences.where('userId').equals(userId).toArray();
  return rows.find((row) => row.deletedAt === null);
}

/**
 * Los avisos que se guardan cuando se crea la fila de preferencias.
 *
 * Exportado, y no escrito dos veces: la pantalla de Avisos tiene que ofrecer lo
 * mismo que se guardaria, y dos copias de un valor por defecto se separan al
 * primer retoque.
 *
 * OJO CON LA DIFERENCIA ENTRE ESTO Y «NO HAY FILA». Mientras no exista fila,
 * `revisarAvisos` NO avisa de nada a proposito —el usuario no ha tenido ocasion
 * de decir que si, y avisar por omision es como se pierde el permiso—. Estos
 * son los valores del momento en que la fila se crea, no lo que rige en su
 * ausencia.
 */
export const AVISOS_POR_DEFECTO: UserPreferences['notificationPrefs'] = {
  enabled: true,
  workoutReminder: true,
  restTimer: true,
  weeklyCheckin: true,
};

export function defaultPreferences(ctx: WriteContext): UserPreferences {
  return {
    ...newSyncColumns(ctx, idDeSingleton(ctx)),
    units: { weight: 'kg', distance: 'km' },
    notificationPrefs: { ...AVISOS_POR_DEFECTO },
    dietary: {
      cuisines: ['spanish', 'mediterranean'],
      excluded: [],
      allergies: [],
      intolerances: [],
    },
    learnedPreferences: [],
    aiEnabled: true,
    aiBudgetCents: 500,
    // Las cuatro que el motor generaba antes de que esto se pudiera elegir, no
    // las cinco: crear la fila no puede cambiarle el dia a nadie.
    mealSlots: [...DEFAULT_MEAL_SLOTS],
    // Sin elegir: la nutricion no calcula nada hasta que el usuario lo decida.
    nutritionGoal: null,
    defaultTrainingTimeOfDay: 'afternoon',
    // El objetivo por defecto es desarrollar musculo: es una aplicacion de
    // culturismo natural y quien la instala quiere eso. Se puede cambiar en
    // `Mas → Objetivo`, y el bloque siguiente lo respeta.
    trainingGoal: 'muscle_gain',
    // Sin contexto declarado se asume recuperacion normal. Lo contrario
    // —suponer que esta mermada— recortaria el volumen de todo el mundo sin
    // que nadie lo hubiera dicho.
    recoveryContext: 'normal',
  };
}

export async function savePreferences(
  ctx: WriteContext,
  patch: Partial<Omit<UserPreferences, keyof ReturnType<typeof newSyncColumns>>>,
): Promise<UserPreferences> {
  const existing = (await getPreferences(ctx.userId)) ?? defaultPreferences(ctx);
  return putRow(
    ctx,
    'userPreferences',
    parchear(userPreferencesSchema, existing, patch, {
      tabla: 'user_preferences',
      id: existing.id,
    }),
  );
}
